import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { executeMutation } from '../../graphql';
import { combinationRecommender } from '../../graphql/mutations';
import './index.module.css';

const PAGING_SIZE = 6;

const Recommendor = ({
  originalCombo,
  vehicleType,
  onSelectRecommendation,
  maxLength,
  selectStylePath = '/create/select-a-style',
  loadingPrimaryText = 'Looking for similar plates...',
  loadingSecondaryText = 'This may take a moment',
  title = 'Similar combinations available'
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!originalCombo || originalCombo.length < 2) return;

    let cancelled = false;
    setLoading(true);
    setShowAll(false);

    executeMutation(combinationRecommender, {
      input: {
        plateNumber: originalCombo.toUpperCase(),
        groupName: '',
        vehicleType: vehicleType === 'car' ? 'Car' : 'Motorbike',
        maxNumberOfRecommendations: 12
      }
    })
      .then((result) => {
        if (cancelled) return;
        const body = result?.recommender?.body;
        if (!body) { setRecommendations([]); return; }
        const parsed = typeof body === 'string' ? JSON.parse(body) : body;
        const recs = parsed.recommendations || parsed.data || parsed || [];
        setRecommendations(
          recs.slice(0, 12).map((rec) => rec.PlateCombo || rec.combo || rec)
        );
      })
      .catch(() => { if (!cancelled) setRecommendations([]); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [originalCombo, vehicleType]);

  if (loading) {
    return (
      <div className="combination-checker__recommender">
        <div className="combination-checker__recommender-loading">
          <p className="combination-checker__recommender-loading-primary">{loadingPrimaryText}</p>
          <p className="combination-checker__recommender-loading-secondary">{loadingSecondaryText}</p>
        </div>
      </div>
    );
  }

  if (recommendations.length === 0) return null;

  const visible = showAll ? recommendations : recommendations.slice(0, PAGING_SIZE);

  return (
    <div className="combination-checker__recommender">
      <p className="combination-checker__recommender-title">{title}</p>
      <div className="combination-checker__recommender-plates">
        {visible.map((rec) => (
          <a
            key={rec}
            className="combination-checker__recommender-plate"
            href={`${selectStylePath}?vehicleType=${vehicleType}&combination=${rec}`}
            onClick={(e) => {
              if (typeof onSelectRecommendation === 'function') {
                e.preventDefault();
                onSelectRecommendation(rec);
              }
            }}
          >
            {rec}
          </a>
        ))}
      </div>
      {recommendations.length > PAGING_SIZE && (
        <button
          type="button"
          className="combination-checker__recommender-paging button button--secondary"
          onClick={() => setShowAll((s) => !s)}
        >
          <span className="button__text">{showAll ? 'Show less' : 'Show more'}</span>
        </button>
      )}
    </div>
  );
};

Recommendor.propTypes = {
  originalCombo: PropTypes.string.isRequired,
  vehicleType: PropTypes.oneOf(['car', 'motorcycle']).isRequired,
  onSelectRecommendation: PropTypes.func,
  maxLength: PropTypes.number.isRequired,
  selectStylePath: PropTypes.string,
  loadingPrimaryText: PropTypes.string,
  loadingSecondaryText: PropTypes.string,
  title: PropTypes.string
};

export default Recommendor;
